import {
  audioExtractionAPIResultAtom,
  audioExtractionTokenAtom,
  categoriesAPIResultsAtom,
  curAnnotationTypesAtom,
  curSelectedRegionAtom,
  curTimeAtom,
  isPlayingAtom,
  saveToAudioApiStatusAtom,
} from 'packages/main/components/audio-tool/atoms';
import { Snapshot, useRecoilCallback, useRecoilValue } from 'recoil';

import {
  AudioAnnotationTypes,
  getSirenCategoryName,
  AnnotationRegion,
  NUM_MICROSEC_IN_A_SEC,
} from 'packages/main/components/audio-tool/audio-tool-types';
import { Region } from 'wavesurfer.js/src/plugin/regions';

import {
  getActiveWaveSurfers,
  updateRegionColor,
} from 'packages/main/components/audio-tool/utils';
import {
  addUpdateAudioAnnotation,
  AudioAnnotation,
  deleteAudioAnnotation,
  useAudioAnnotationsForExtractionSWR,
} from 'packages/main/components/audio-tool/api';
import { getSirenCategoryTokenFromName } from 'packages/main/components/audio-tool/utils';
import { REQ_STATUS } from 'packages/lib/common-types/types';

export const useDeselectCurRegion = (): (() => Promise<void>) => {
  const seekAllToTime = useSeekAllToTime();
  const stopAllWavesurfers = useStopAllWavesurfers();
  const pausePlayAllAudio = usePausePlayAllAudio();

  return useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const prevSelectedRegion = await snapshot.getPromise(curSelectedRegionAtom);
    if (prevSelectedRegion) {
      updateRegionColor(
        prevSelectedRegion,
        false,
        prevSelectedRegion.firedIn,
        false
      );

      // Glitch fix: Select a region and play. Push Esc to deselect before it finiishes playing. Pause and play. Glitch: cursors across waveforms are at different positions, since playback of a region updates only that region's cursor, while playback of all syncs them.
      const wasPlaying = await snapshot.getPromise(isPlayingAtom);
      const curTime = prevSelectedRegion.wavesurfer.getCurrentTime();
      await stopAllWavesurfers();
      set(isPlayingAtom, false);

      // Glitch fix: cursors with other waveforms might be out of sync.
      const curStartPercent =
        curTime / prevSelectedRegion.wavesurfer.getDuration();
      await seekAllToTime(curTime, curStartPercent);

      // Now resume playback if appropriate
      if (wasPlaying) {
        await pausePlayAllAudio(true);
      }
    }

    set(curSelectedRegionAtom, null);
  });
};

export const useSeekAllToTime = (): ((
  newCurTime: number,
  seekToPercent: number
) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (newCurTime: number, seekToPercent: number): Promise<void> => {
        const curAnnotationTypes = await snapshot.getPromise(
          curAnnotationTypesAtom
        );
        const allWaveSurfers = await getActiveWaveSurfers(snapshot, [
          ...curAnnotationTypes,
          AudioAnnotationTypes.NoAnnotations,
        ]);

        set(curTimeAtom, newCurTime);

        allWaveSurfers.forEach(async (waveSurfer) => {
          if (waveSurfer) {
            const curSeek =
              waveSurfer.getCurrentTime() / waveSurfer.getDuration();
            // Make sure we're not already at the desired location (taking into account floating point rounding error)
            if (Math.abs(curSeek - seekToPercent) > 0.00001) {
              // Prevent this seekTo action from emitting a 'seek'... which would trigger this callback again
              waveSurfer.setDisabledEventEmissions(['seek', 'interaction']);
              await waveSurfer.seekTo(seekToPercent);
              waveSurfer.setDisabledEventEmissions([]);
            }
          }
        });
      }
  );
};

export const useStopAllWavesurfers = (): (() => Promise<void>) => {
  return useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const curAnnotationTypes = await snapshot.getPromise(
      curAnnotationTypesAtom
    );
    const allWaveSurfers = await getActiveWaveSurfers(snapshot, [
      ...curAnnotationTypes,
      AudioAnnotationTypes.NoAnnotations,
    ]);

    allWaveSurfers.forEach(async (waveSurfer, idx) => {
      if (waveSurfer) {
        // Normally, a stop event fires 'seek' events, resetting the waveform to time 0.0, and causing various glitches. We temporarily disable 'seek' events to avoid those problems.
        waveSurfer.setDisabledEventEmissions(['seek', 'interaction']);
        await waveSurfer.stop();
        waveSurfer.setDisabledEventEmissions([]);
      }
    });

    set(isPlayingAtom, false);
  });
};

export const usePausePlayAllAudio = (): ((play: boolean) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (play: boolean): Promise<void> => {
        const curAnnotationTypes = await snapshot.getPromise(
          curAnnotationTypesAtom
        );
        const allWaveSurfers = await getActiveWaveSurfers(snapshot, [
          ...curAnnotationTypes,
          AudioAnnotationTypes.NoAnnotations,
        ]);

        for (let idx = 0; idx < allWaveSurfers.length; idx++) {
          const waveSurfer = allWaveSurfers[idx];
          if (waveSurfer) {
            // We have to unmute various waveSurfers when the user plays a region. Since there is no 'Done playing' event for us to re-mute when the region
            // is done playing, we have to restore mute of all (but one) wavesurfer here.
            waveSurfer.setMute(idx >= 1);

            if (play) {
              await waveSurfer.play();
            } else {
              await waveSurfer.pause();
            }
          }
        }

        set(isPlayingAtom, play);
      }
  );
};

export const useHandleRegionMouseClick = (): ((
  region: Region,
  mouseEvent: MouseEvent
) => Promise<void>) => {
  return useRecoilCallback(
    ({ set, snapshot }) =>
      async (region: Region, mouseEvent: MouseEvent): Promise<void> => {
        // First: if a different region was previously selected, change its color back to normal
        const prevSelectedRegion = await snapshot.getPromise(
          curSelectedRegionAtom
        );
        if (prevSelectedRegion) {
          updateRegionColor(prevSelectedRegion, false, region.firedIn, false);
        }

        set(curSelectedRegionAtom, region);
        updateRegionColor(region, true, region.firedIn, false);

        // DISABLED per Lisa
        // If we ever want clicks to just select the region, but not change playback (cursor) position, uncomment this code.
        //   mouseEvent.stopPropagation();
      }
  );
};

export const useHandleRegionMouseOver = (): ((
  region: Region
) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot }) =>
      async (region: Region): Promise<void> => {
        const regionIsSelected = await isRegionSelected(region, snapshot);
        updateRegionColor(region, regionIsSelected, region.firedIn, true);
      }
  );
};

export const useHandleRegionMouseLeave = (): ((
  region: Region
) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot }) =>
      async (region: Region): Promise<void> => {
        // Restore region's normal color during normal playback when cursor leaves region
        const regionIsSelected = await isRegionSelected(region, snapshot);
        updateRegionColor(region, regionIsSelected, region.firedIn, false);
      }
  );
};

async function isRegionSelected(
  region: Region,
  snapshot: Snapshot
): Promise<boolean> {
  const curSelectedRegion = await snapshot.getPromise(curSelectedRegionAtom);
  return !!(curSelectedRegion && curSelectedRegion.id === region.id);
}

// Called when playback cursor enters a region (including when user pushes Play Region button)
export const useHandleRegionIn = (): ((region: Region) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (region: Region): Promise<void> => {
        // Restore region's normal color during normal playback when cursor leaves region
        const regionIsSelected = await isRegionSelected(region, snapshot);
        updateRegionColor(region, regionIsSelected, region.firedIn, false);
      }
  );
};

/**  The 'region-out' event is fired in two circumstances:
   1) Audio is playing back normally, and the cursor exits this region (and playback continues)
   2) Only a region is playing back, and playback finishes.
   Since regions do not fire their own 'region-finished' event, we must tap into this event to know when a region has
   finished playback back.
  */
export const useHandleRegionOut = (): ((region: Region) => Promise<void>) => {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (region: Region): Promise<void> => {
        const curSelectedRegion = await snapshot.getPromise(
          curSelectedRegionAtom
        );

        if (curSelectedRegion && region.id === curSelectedRegion.id) {
          set(isPlayingAtom, false);
        } else {
          // Restore region's normal color during normal playback when cursor leaves region
          const regionIsSelected = await isRegionSelected(region, snapshot);
          updateRegionColor(region, regionIsSelected, region.firedIn, false);
        }
      }
  );
};

// Since the region-created event fires instantly, before a user has finished dragging it, we instead use the region-update-end event to catch
// both newly-created (and finished dragging) region events, as well as modification of existing ones.
export const useHandleRegionCreatedOrModified = (): ((
  region: AnnotationRegion,
  annotationType: AudioAnnotationTypes
) => Promise<void>) => {
  const audioExtractionToken = useRecoilValue(audioExtractionTokenAtom);
  const { mutate: reloadAudioAnnotations } =
    useAudioAnnotationsForExtractionSWR(audioExtractionToken);

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        region: AnnotationRegion,
        annotationType: AudioAnnotationTypes
      ): Promise<void> => {
        const audioExtractionToken = await snapshot.getPromise(
          audioExtractionTokenAtom
        );
        const categories = await snapshot.getPromise(categoriesAPIResultsAtom);
        const audioExtraction = await snapshot.getPromise(
          audioExtractionAPIResultAtom
        );

        region.annotationType = annotationType;

        const regionIsSelected = await isRegionSelected(region, snapshot);
        updateRegionColor(region, regionIsSelected, region.firedIn, false);

        const sirenCategoryName = getSirenCategoryName(annotationType);

        if (categories && audioExtraction) {
          set(saveToAudioApiStatusAtom, REQ_STATUS.pending);

          const sirenCategoryToken = getSirenCategoryTokenFromName(
            sirenCategoryName,
            categories
          );

          const audioAnnotation = new AudioAnnotation(
            audioExtractionToken,
            Math.round(region.start * NUM_MICROSEC_IN_A_SEC) +
              audioExtraction.startTimestamp,
            Math.round(region.end * NUM_MICROSEC_IN_A_SEC) +
              audioExtraction.startTimestamp,
            sirenCategoryName,
            sirenCategoryToken,
            region.token
          );

          // Bug Fix: When updating an existing region that is currently selected, trigger a change event for those watching curSelectedRegionAtom
          // That way, the controls showing the starting & ending range for the currently-selected region get updated
          const curSelectedRegion = await snapshot.getPromise(
            curSelectedRegionAtom
          );
          if (
            region.token &&
            curSelectedRegion &&
            curSelectedRegion.id === region.id
          ) {
            set(curSelectedRegionAtom, null);
            set(curSelectedRegionAtom, region);
          }

          addUpdateAudioAnnotation(audioAnnotation)
            .then((token) => {
              region.token = token;
              set(saveToAudioApiStatusAtom, (prev) =>
                prev === REQ_STATUS.error ? prev : REQ_STATUS.success
              );

              void reloadAudioAnnotations();
            })
            .catch((reason) => {
              set(saveToAudioApiStatusAtom, REQ_STATUS.error);
              console.warn(
                'Audio annotation could not be added/updated to backend API: ',
                reason
              );
            });
        } else {
          set(saveToAudioApiStatusAtom, REQ_STATUS.error);
          if (!categories)
            console.warn(
              'Annotation could not be created/modified because Categories was not loaded from API!'
            );
          else
            console.warn(
              'Annotation could not be created/modified because the audioExtraction was not loaded from API.'
            );
        }
      }
  );
};

// Regions are deleted when:
// a) The user selects a region, then deletes it (via Delete button or keyboard shortcut)
// b) The user clicks the Delete All Regions button for a wavesurfer row
// c) The user removes an Annotation Category (all regions in that category are deleted)
// Since Wavesurfer sends us an event once a region has been deleted, all of the above just delete the region from wavesurfer.
// Then this callback, called by WaveSurfer on delete, handles updating the backend API.
export const useHandleRegionDeleted = (): ((
  region: AnnotationRegion,
  annotationType: AudioAnnotationTypes
) => Promise<void>) => {
  const audioExtractionToken = useRecoilValue(audioExtractionTokenAtom);
  const { mutate: reloadAudioAnnotations } =
    useAudioAnnotationsForExtractionSWR(audioExtractionToken);

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        region: AnnotationRegion,
        annotationType: AudioAnnotationTypes
      ): Promise<void> => {
        // Bug fix: if user deletes the currently-selected region, de-select it, so player controls change back.
        const curSelectedRegion = await snapshot.getPromise(
          curSelectedRegionAtom
        );

        if (curSelectedRegion?.id === region.id) {
          set(curSelectedRegionAtom, null);
        }

        if (region.token) {
          set(saveToAudioApiStatusAtom, REQ_STATUS.pending);
          deleteAudioAnnotation(region.token)
            .then((resp) => {
              set(saveToAudioApiStatusAtom, (prev) =>
                prev === REQ_STATUS.error ? prev : REQ_STATUS.success
              );

              void reloadAudioAnnotations();
            })
            .catch((reason) => {
              set(saveToAudioApiStatusAtom, REQ_STATUS.error);
              console.warn(
                'Audio annotation could not be deleted from server.',
                annotationType,
                reason
              );
            });
        }
      }
  );
};

export const useInstallWaveSurferCallbacks = (): ((
  waveSurferObj: WaveSurfer | null,
  annotationType: AudioAnnotationTypes
) => Promise<void>) => {
  const seekAllToTime = useSeekAllToTime();
  const handleRegionMouseClick = useHandleRegionMouseClick();
  const handleRegionMouseOver = useHandleRegionMouseOver();
  const handleRegionIn = useHandleRegionIn();
  const handleRegionOut = useHandleRegionOut();
  const handleRegionMouseLeave = useHandleRegionMouseLeave();
  const handleRegionCreatedOrModified = useHandleRegionCreatedOrModified();
  const handleRegionDeleted = useHandleRegionDeleted();

  // Note: various callbacks are not included in this deps array, because they are all useRecoilCallbacks which never change.
  return useRecoilCallback(
    ({ set }) =>
      async (
        waveSurferObj: WaveSurfer | null,
        annotationType: AudioAnnotationTypes
      ): Promise<void> => {
        if (waveSurferObj) {
          waveSurferObj.on('seek', async function (curProgressPercent: number) {
            if (waveSurferObj) {
              const newCurTime = waveSurferObj.getCurrentTime();
              await seekAllToTime(newCurTime, curProgressPercent);
              // Bug fix: can no longer deselect current region here, as that causes instead deselect immediately after user clicks on a region,
              /// now that we allow a click in a region to not only select that region, but ALSO seek to the clicked time.
              //      await deselectCurRegion();
            }
          });

          waveSurferObj.on(
            'region-click',
            (region: Region, mouseEvent: MouseEvent) => {
              void handleRegionMouseClick(region, mouseEvent);
            }
          );

          // Highlight during playback when cursor enters region
          waveSurferObj.on(
            'region-in',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionIn(region);
            }
          );

          // *** See comments for useHandleRegionOut!! ***
          waveSurferObj.on(
            'region-out',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionOut(region);
            }
          );

          // Highlight on mouseover
          waveSurferObj.on(
            'region-mouseenter',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionMouseOver(region);
            }
          );

          // Restore normal region color on mouse leave
          waveSurferObj.on(
            'region-mouseleave',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionMouseLeave(region);
            }
          );

          // Called when a new region is created OR an existing region is updated.
          waveSurferObj.on(
            'region-update-end',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionCreatedOrModified(region, annotationType);
            }
          );

          // Called anytime a region is deleted... both when user deletes the currently-selected region AND when user deletes all regions for an entire category
          waveSurferObj.on(
            'region-removed',
            (region: Region, mouseEvent: unknown) => {
              void handleRegionDeleted(region, annotationType);
            }
          );

          waveSurferObj.on('finish', (a) => {
            set(isPlayingAtom, false);
          });
        }
      }
  );
};
